import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import { ResponsiveSignatureCanvas } from './components/shared/ResponsiveSignatureCanvas';
import { useZipAutofill } from './hooks/useZipAutofill';
import { t } from './i18n/portalTranslations';

const MedicalRecordsRequestForm = ({ isOpen, onClose, currentUser, existingRequest = null, onSuccess, sessionToken }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    requester_type: existingRequest?.requester_type || 'self',
    parent_guardian_name: existingRequest?.parent_guardian_name || '',
    child_name: existingRequest?.child_name || '',
    requester_address: existingRequest?.requester_address || '',
    requester_city: existingRequest?.requester_city || '',
    requester_state: existingRequest?.requester_state || '',
    requester_zip: existingRequest?.requester_zip || '',
    requester_phone: existingRequest?.requester_phone || '',
    requester_email: existingRequest?.requester_email || currentUser?.email || '',
    date_range_all: existingRequest?.date_range_all ?? true,
    date_range_start: existingRequest?.date_range_start || '',
    date_range_end: existingRequest?.date_range_end || '',
    record_types: existingRequest?.record_types || [],
    // New filtering fields
    filter_by_provider: existingRequest?.filter_by_provider || false,
    selected_provider_id: existingRequest?.selected_provider_id || '',
    filter_by_service_type: existingRequest?.filter_by_service_type || false,
    selected_service_types: existingRequest?.selected_service_types || [],
    delivery_method: existingRequest?.delivery_method || 'portal_download',
    fax_number: existingRequest?.fax_number || '',
    third_party_recipient_name: existingRequest?.third_party_recipient_name || '',
    third_party_recipient_relationship: existingRequest?.third_party_recipient_relationship || '',
    third_party_recipient_phone: existingRequest?.third_party_recipient_phone || '',
    primary_signature: existingRequest?.primary_signature || '',
    release_authorization_signature: existingRequest?.release_authorization_signature || ''
  });
  
  const [signaturePad, setSignaturePad] = useState(null);
  const [thirdPartySignaturePad, setThirdPartySignaturePad] = useState(null);
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  
  // Reset form when existingRequest changes or dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        requester_type: existingRequest?.requester_type || 'self',
        parent_guardian_name: existingRequest?.parent_guardian_name || '',
        child_name: existingRequest?.child_name || '',
        requester_address: existingRequest?.requester_address || '',
        requester_city: existingRequest?.requester_city || '',
        requester_state: existingRequest?.requester_state || '',
        requester_zip: existingRequest?.requester_zip || '',
        requester_phone: existingRequest?.requester_phone || '',
        requester_email: existingRequest?.requester_email || currentUser?.email || '',
        date_range_all: existingRequest?.date_range_all ?? true,
        date_range_start: existingRequest?.date_range_start || '',
        date_range_end: existingRequest?.date_range_end || '',
        record_types: existingRequest?.record_types || [],
        filter_by_provider: existingRequest?.filter_by_provider || false,
        selected_provider_id: existingRequest?.selected_provider_id || '',
        filter_by_service_type: existingRequest?.filter_by_service_type || false,
        selected_service_types: existingRequest?.selected_service_types || [],
        delivery_method: existingRequest?.delivery_method || 'portal_download',
        fax_number: existingRequest?.fax_number || '',
        third_party_recipient_name: existingRequest?.third_party_recipient_name || '',
        third_party_recipient_relationship: existingRequest?.third_party_recipient_relationship || '',
        third_party_recipient_phone: existingRequest?.third_party_recipient_phone || '',
        primary_signature: existingRequest?.primary_signature || '',
        release_authorization_signature: existingRequest?.release_authorization_signature || ''
      });
      setStep(1); // Reset to first step
    }
  }, [isOpen, existingRequest, currentUser]);
  
  // Service types from CAC Worcester website
  const serviceTypes = [
    // General Outpatient Services
    'Mental Health Evaluations',
    'Outpatient Therapy',
    'Medication Management',
    'Community Support Program (CSP)',
    
    // Child, Adolescent & CBHI Services
    'In Home Behavioral Services (IHBS)',
    'In Home Therapy (IHT)',
    'Therapeutic Mentoring (TM)',
    
    // Substance Addiction Recovery Services
    'Outpatient Group Therapy',
    'SOAP (Structured Outpatient Addiction Program)',
    'Adolescent SOAP',
    'Second Offender Aftercare (SOA)',
    'First Offender Massachusetts Impaired Driver Program (MID)',
    'Substance Addiction Evaluations'
  ];

  const recordTypeOptions = [
    { value: 'all_records', label: 'All Records' },
    { value: 'progress_notes', label: 'Progress Notes' },
    { value: 'assessments', label: 'Assessments' },
    { value: 'treatment_plans', label: 'Treatment Plans' },
    { value: 'consents', label: 'Consents' },
    { value: 'diagnoses', label: 'Diagnoses' },
    { value: 'medications', label: 'Medications' },
    { value: 'vitals', label: 'Vitals' },
    { value: 'allergies', label: 'Allergies' },
    { value: 'referrals', label: 'Referrals' },
    { value: 'lab_results', label: 'Lab Results' },
    { value: 'billing_records', label: 'Billing Records' },
    { value: 'appointment_history', label: 'Appointment History' },
    { value: 'client_documents', label: 'Uploaded Documents' },
  ];

  const deliveryOptions = [
    { value: 'portal_download', label: 'Portal Download (FREE - Available 14 days)', price: 0 },
    { value: 'fax', label: 'Fax ($5)', price: 5 },
    { value: 'pickup', label: 'Pickup ($10)', price: 10 },
    { value: 'mail', label: 'Mail ($15)', price: 15 }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // June 2026 — autofill requester city/state from ZIP (MA dataset).
  const zipAutofill = useZipAutofill({
    formData, setField: handleInputChange,
    zipField: 'requester_zip', cityField: 'requester_city', stateField: 'requester_state',
  });

  const handleRecordTypeToggle = (type) => {
    setFormData(prev => {
      const currentTypes = prev.record_types;
      if (type === 'all_records') {
        return { ...prev, record_types: ['all_records'] };
      }
      
      const filtered = currentTypes.filter(t => t !== 'all_records');
      if (filtered.includes(type)) {
        return { ...prev, record_types: filtered.filter(t => t !== type) };
      } else {
        return { ...prev, record_types: [...filtered, type] };
      }
    });
  };

  const handleServiceTypeToggle = (serviceType) => {
    setFormData(prev => {
      const current = prev.selected_service_types;
      if (current.includes(serviceType)) {
        return { ...prev, selected_service_types: current.filter(t => t !== serviceType) };
      } else {
        return { ...prev, selected_service_types: [...current, serviceType] };
      }
    });
  };

  // Fetch providers and service types
  React.useEffect(() => {
    if (isOpen && currentUser) {
      fetchClientProviders();
    }
  }, [isOpen, currentUser]);

  const fetchClientProviders = async () => {
    try {
      // Fetch notes for the current client to get their providers
      const notesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notes?client_id=${currentUser.id}`, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      });
      
      if (notesRes.ok) {
        const notes = await notesRes.json();
        // Get unique provider IDs
        const providerIds = [...new Set(notes.map(note => note.provider_id).filter(Boolean))];
        
        if (providerIds.length > 0) {
          // Fetch provider details
          const usersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
            headers: {
              'Authorization': `Bearer ${sessionStorage.getItem('token')}`
            }
          });
          if (usersRes.ok) {
            const users = await usersRes.json();
            const clientProviders = users.filter(u => providerIds.includes(u.id));
            setProviders(clientProviders);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching client providers:', error);
    }
  };

  const handleSubmit = async () => {
    // Validate signature
    if (!signaturePad || signaturePad.isEmpty()) {
      alert('Please provide your signature before submitting');
      return;
    }

    // Validate third party signature if needed
    if (formData.third_party_recipient_name && (!thirdPartySignaturePad || thirdPartySignaturePad.isEmpty())) {
      alert('Please provide third party release signature before submitting');
      return;
    }

    // Jun 2026 — a request without record types produced an empty
    // review screen for the records manager.  Require at least one.
    if (!formData.record_types || formData.record_types.length === 0) {
      alert('Please select at least one record type to request');
      return;
    }

    setLoading(true);
    try {
      // Capture signatures as base64
      const primarySignatureData = signaturePad.toDataURL('image/png');
      const thirdPartySignatureData = formData.third_party_recipient_name && thirdPartySignaturePad 
        ? thirdPartySignaturePad.toDataURL('image/png')
        : null;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/medical-records/request?token=${sessionToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          primary_signature: primarySignatureData,
          primary_signature_date: new Date().toISOString(),
          release_authorization_signature: thirdPartySignatureData,
          release_authorization_date: formData.third_party_recipient_name ? new Date().toISOString() : null
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`Request ${existingRequest ? 'updated' : 'submitted'} successfully! Request ID: ${data.request_id}\n${data.payment_required ? `Fee: $${data.fee_amount_usd}` : 'No fee required'}`);
        if (onSuccess) onSuccess();
        onClose();
      } else {
        alert('Error submitting request');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error submitting request');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{t('Requester Information')}</h3>
      
      <div>
        <label className="block text-sm font-medium mb-1">{t('I am requesting records for:')}</label>
        <select
          value={formData.requester_type}
          onChange={(e) => handleInputChange('requester_type', e.target.value)}
          className="w-full border rounded p-2"
        >
          <option value="self">{t('Myself')}</option>
          <option value="parent_guardian">{t('My Child (Parent/Guardian)')}</option>
        </select>
      </div>

      {formData.requester_type === 'parent_guardian' && (
        <>
          <input
            type="text"
            placeholder={t('Parent/Guardian Name')}
            value={formData.parent_guardian_name}
            onChange={(e) => handleInputChange('parent_guardian_name', e.target.value)}
            className="w-full border rounded p-2"
          />
          <input
            type="text"
            placeholder={t("Child's Name")}
            value={formData.child_name}
            onChange={(e) => handleInputChange('child_name', e.target.value)}
            className="w-full border rounded p-2"
          />
        </>
      )}

      <input
        type="text"
        placeholder={t('Street Address *')}
        value={formData.requester_address}
        onChange={(e) => handleInputChange('requester_address', e.target.value)}
        className="w-full border rounded p-2"
        required
      />
      
      <div className="grid grid-cols-3 gap-2">
        <input
          type="text"
          placeholder={t('City *')}
          value={formData.requester_city}
          onChange={(e) => handleInputChange('requester_city', e.target.value)}
          className="border rounded p-2"
          required
        />
        <input
          type="text"
          placeholder={t('State *')}
          value={formData.requester_state}
          onChange={(e) => handleInputChange('requester_state', e.target.value)}
          className="border rounded p-2"
          maxLength="2"
          required
        />
        <input
          type="text"
          placeholder={t('ZIP *')}
          value={formData.requester_zip}
          onChange={(e) => zipAutofill.handleZipChange(e.target.value)}
          className="border rounded p-2"
          required
          data-testid="records-request-requester-zip"
        />
      </div>
      {zipAutofill.autofillStatus === 'filled' && (
        <p className="text-xs text-emerald-600 mt-1" data-testid="records-request-zip-autofilled">
          Auto-filled city/state from ZIP.
        </p>
      )}

      <input
        type="tel"
        placeholder={t('Phone Number *')}
        value={formData.requester_phone}
        onChange={(e) => handleInputChange('requester_phone', e.target.value)}
        className="w-full border rounded p-2"
        required
      />

      <input
        type="email"
        placeholder={t('Email Address *')}
        value={formData.requester_email}
        onChange={(e) => handleInputChange('requester_email', e.target.value)}
        className="w-full border rounded p-2"
        required
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{t('What Records Do You Need?')}</h3>
      
      <div>
        <label className="block text-sm font-medium mb-2">{t('Date Range:')}</label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="radio"
              checked={formData.date_range_all}
              onChange={() => handleInputChange('date_range_all', true)}
              className="mr-2"
            />
            All dates
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              checked={!formData.date_range_all}
              onChange={() => handleInputChange('date_range_all', false)}
              className="mr-2"
            />
            Specific date range
          </label>
        </div>
        
        {!formData.date_range_all && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <input
              type="date"
              value={formData.date_range_start}
              onChange={(e) => handleInputChange('date_range_start', e.target.value)}
              className="border rounded p-2"
            />
            <input
              type="date"
              value={formData.date_range_end}
              onChange={(e) => handleInputChange('date_range_end', e.target.value)}
              className="border rounded p-2"
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('Record Types: *')}</label>
        <div className="space-y-2">
          {recordTypeOptions.map(option => (
            <label key={option.value} className="flex items-center">
              <input
                type="checkbox"
                checked={formData.record_types.includes(option.value)}
                onChange={() => handleRecordTypeToggle(option.value)}
                className="mr-2"
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>

      {/* Filter by Provider */}
      <div className="border-t pt-4">
        <label className="flex items-center mb-3">
          <input
            type="checkbox"
            checked={formData.filter_by_provider}
            onChange={(e) => handleInputChange('filter_by_provider', e.target.checked)}
            className="mr-2"
          />
          <span className="font-medium">Filter by Specific Provider</span>
        </label>
        
        {formData.filter_by_provider && (
          <select
            value={formData.selected_provider_id}
            onChange={(e) => handleInputChange('selected_provider_id', e.target.value)}
            className="w-full border rounded p-2"
          >
            <option value="">{t('Select a provider...')}</option>
            {providers.map(provider => (
              <option key={provider.id} value={provider.id}>
                {provider.full_name} {provider.credentials ? `(${provider.credentials})` : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Filter by Service Type */}
      <div className="border-t pt-4">
        <label className="flex items-center mb-3">
          <input
            type="checkbox"
            checked={formData.filter_by_service_type}
            onChange={(e) => handleInputChange('filter_by_service_type', e.target.checked)}
            className="mr-2"
          />
          <span className="font-medium">Filter by Service Type</span>
        </label>
        
        {formData.filter_by_service_type && (
          <div className="space-y-2 pl-6 max-h-60 overflow-y-auto border rounded p-3 bg-gray-50">
            {serviceTypes.map(serviceType => (
              <label key={serviceType} className="flex items-start">
                <input
                  type="checkbox"
                  checked={formData.selected_service_types.includes(serviceType)}
                  onChange={() => handleServiceTypeToggle(serviceType)}
                  className="mr-2 mt-1"
                />
                <span className="text-sm">{serviceType}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{t('Delivery Method')}</h3>
      
      <div className="space-y-2">
        {deliveryOptions.map(option => (
          <label key={option.value} className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
            <div className="flex items-center">
              <input
                type="radio"
                checked={formData.delivery_method === option.value}
                onChange={() => handleInputChange('delivery_method', option.value)}
                className="mr-3"
              />
              <span>{option.label}</span>
            </div>
            {option.price > 0 && (
              <span className="text-green-600 font-semibold">${option.price}</span>
            )}
          </label>
        ))}
      </div>

      {formData.delivery_method === 'fax' && (
        <input
          type="tel"
          placeholder={t('Fax Number *')}
          value={formData.fax_number}
          onChange={(e) => handleInputChange('fax_number', e.target.value)}
          className="w-full border rounded p-2"
        />
      )}

      <div className="bg-blue-50 border border-blue-200 p-3 rounded">
        <p className="text-sm text-blue-800">
          {formData.delivery_method === 'portal_download' && "Your records will be available for download in the portal for 14 days after approval."}
          {formData.delivery_method === 'fax' && "Records will be faxed to the number you provide. $5 processing fee applies."}
          {formData.delivery_method === 'pickup' && "Records will be printed and ready for pickup. $10 printing fee applies."}
          {formData.delivery_method === 'mail' && "Records will be printed and mailed to your address. $15 fee applies (includes postage)."}
        </p>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{t('Third Party Release (Optional)')}</h3>
      <p className="text-sm text-gray-600">Only complete if sending records to someone else</p>
      
      <input
        type="text"
        placeholder={t('Recipient Name')}
        value={formData.third_party_recipient_name}
        onChange={(e) => handleInputChange('third_party_recipient_name', e.target.value)}
        className="w-full border rounded p-2"
      />
      
      <input
        type="text"
        placeholder={t('Relationship')}
        value={formData.third_party_recipient_relationship}
        onChange={(e) => handleInputChange('third_party_recipient_relationship', e.target.value)}
        className="w-full border rounded p-2"
      />
      
      <input
        type="tel"
        placeholder={t('Recipient Phone')}
        value={formData.third_party_recipient_phone}
        onChange={(e) => handleInputChange('third_party_recipient_phone', e.target.value)}
        className="w-full border rounded p-2"
      />
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{t('Authorization & Signature')}</h3>
      
      <div className="bg-gray-50 p-4 rounded text-sm space-y-2 max-h-60 overflow-y-auto">
        <p className="font-semibold">HIPAA Authorization for Release of Medical Records</p>
        <p>I authorize the practice identified in this request to release my protected health information as described above.</p>
        <p>I understand that:</p>
        <ul className="list-disc ml-6 space-y-1">
          <li>I have the right to revoke this authorization in writing at any time</li>
          <li>Treatment, payment, enrollment, or eligibility for benefits may not be conditioned on signing this authorization</li>
          <li>Information released may be subject to redisclosure and no longer protected</li>
          <li>This authorization expires 90 days from the date signed</li>
          <li>I may request a copy of this signed authorization</li>
        </ul>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('Primary Signature *')}</label>
        {existingRequest?.primary_signature ? (
          <div>
            <div className="border-2 rounded-lg p-2 bg-gray-50 mb-2">
              <img 
                src={existingRequest.primary_signature} 
                alt="Existing Signature" 
                className="w-full h-[120px] object-contain"
              />
            </div>
            <p className="text-sm text-gray-600 mb-2">Existing signature on file. You can re-sign below to replace it.</p>
          </div>
        ) : null}
        <div className="border-2 border-dashed rounded-lg p-2 bg-white">
          <ResponsiveSignatureCanvas
            ref={(ref) => setSignaturePad(ref)}
            height={120}
            minWidth={1}
            maxWidth={3}
          />
        </div>
        <button
          type="button"
          onClick={() => signaturePad?.clear()}
          className="mt-2 text-sm text-blue-600 hover:text-blue-800"
        >
          Clear Signature
        </button>
      </div>

      {formData.third_party_recipient_name && (
        <div>
          <label className="block text-sm font-medium mb-2">{t('Third Party Release Signature *')}</label>
          <div className="border-2 border-dashed rounded-lg p-2 bg-white">
            <ResponsiveSignatureCanvas
              ref={(ref) => setThirdPartySignaturePad(ref)}
              height={120}
              minWidth={1}
              maxWidth={3}
            />
          </div>
          <button
            type="button"
            onClick={() => thirdPartySignaturePad?.clear()}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800"
          >
            Clear Signature
          </button>
        </div>
      )}

      <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
        <p className="text-sm text-yellow-800">
          By signing this form, you are authorizing the release of your medical records and certify that the information provided is accurate.
        </p>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existingRequest ? t('Edit Medical Records Request') : t('Request Medical Records')}</DialogTitle>
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map(num => (
                <div
                  key={num}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                    step === num ? 'bg-blue-600 text-white' :
                    step > num ? 'bg-green-600 text-white' :
                    'bg-gray-200 text-gray-600'
                  }`}
                >
                  {num}
                </div>
              ))}
            </div>
            <span className="text-sm text-gray-500">{t('Step')} {step} {t('of')} 5</span>
          </div>
        </DialogHeader>

        <div className="mt-6">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
        </div>

        <div className="flex justify-between mt-6">
          <div className="flex gap-2">
            <button
              onClick={() => setStep(step - 1)}
              disabled={step === 1}
              className="px-4 py-2 border rounded disabled:opacity-50 hover:bg-gray-50"
            >
              {t('Previous')}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              {t('Cancel')}
            </button>
          </div>
          
          {step < 5 ? (
            <button
              onClick={() => setStep(step + 1)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              {t('Next')}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? t('Submitting...') : existingRequest ? t('Update Request') : t('Submit Request')}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MedicalRecordsRequestForm;
